import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { unionBy, isEmpty, isEqual } from 'lodash'
import {
	Box,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	Skeleton,
	Stack,
	TextField,
} from '@mui/material'
import { useDispatch } from 'react-redux'

import { ENV_ACTIONS } from '../../../../../reducers/environmentReducer'
import useTileQuery from '../../hooks/useTileQuery'
import { TYPES_OF_TILE } from '../../../../../constants'
import CheckboxList from 'components/pages/dis-v2/_components/CheckboxList'
import { mobileUnitsApi } from 'apis/disApi/viewWizard/mobileUnitsViewApi'
import { isNullOrUndefined, logErrorMessage } from 'utils/functions/helpers'
import { getUserVariables } from 'components/core/services/environmentService'
import OtherOptions, {
	DEFAULT_CHECKED_OPTIONS,
} from 'components/pages/dis-v2/_components/OtherOptions'

const MIN_REPEAT_RELOAD_MAP_VALUE = 5
const MAX_REPEAT_RELOAD_MAP_VALUE = 60
const DEFAULT_REPEAT_RELOAD_MAP_SECONDS = 10

export const mobileUnitTilePrimaryKeyColumns = [
	{
		title: 'Mobile number',
		name: 'Mobile_number',
		mobileUnitKey: 'number',
		hide: false,
		type: 'STRING',
	},
	{
		title: 'Nickname',
		name: 'Nickname',
		mobileUnitKey: 'name',
		hide: false,
		type: 'STRING',
	},
	{
		title: 'Email',
		name: 'Email',
		mobileUnitKey: 'email',
		hide: false,
		type: 'STRING',
	},
	{
		title: 'Email',
		name: 'Email',
		mobileUnitKey: 'email',
		hide: false,
	},
	{
		title: 'Timesheet Status',
		name: 'Timesheet_status',
		mobileUnitKey: 'timesheetStatus',
		hide: false,
		type: 'STRING',
	},
	{
		title: 'GPS Status',
		name: 'Gps_status',
		mobileUnitKey: 'gpsStatus',
		hide: false,
		type: 'STRING',
	},
	{
		title: 'Address',
		name: 'Address',
		mobileUnitKey: 'address',
		hide: false,
		type: 'STRING',
	},
]

function MobileUnitsForm(props) {
	const {
		dashboardKey,
		filterData,
		enabledFetchTileList = true,
		datagridListWhenCreating = [],
		onFilterDataChange,
		environment,
	} = props

	const { t } = useTranslation('common')
	const dispatch = useDispatch()

	const defaultCheckedAdditional = ['Mobile_number', 'Nickname']
	const [primaryKeyList, setPrimaryKeyList] = useState([])
	const [additionalFields, setAdditionalFields] = useState([])
	const [userVariables, setUserVariabless] = useState([])
	const [displayAdditionalFields, setDisplayAdditionalFields] = useState([])

	const [repeatReloadMapError, setRepeatReloadMapError] = useState('')
	const { tileList, isLoading } = useTileQuery({
		dashboardKey,
		enableConfig: enabledFetchTileList,
	})

	const OTHER_OPTIONS = [
		{ label: t('common:misc.expandAllMobileGroups'), value: 'expandAllMobileGroups' },
	]

	const dataGridTile = useMemo(() => {
		const currentList =
			tileList?.length > 0 ? tileList?.filter((tile) => tile?.type === TYPES_OF_TILE.DATA_GRID) : []

		return unionBy(currentList, datagridListWhenCreating, 'name')
	}, [tileList, datagridListWhenCreating])

	const handleSelectChange = (e) => {
		const key = e.target.value
		const name = dataGridTile.find((tile) => tile?.key === key)?.name
		const selectedTile = dataGridTile.find((tile) => tile?.key === key)
		onFilterDataChange('connectedDatagridKey', key)
		if (selectedTile?.is_creating) {
			onFilterDataChange('connectedNameWithNewDatagrid', name)
		}
	}

	useEffect(() => {
		if (filterData?.primaryKeyList) {
			setPrimaryKeyList(filterData?.primaryKeyList)
		}
	}, [filterData?.primaryKeyList])

	useEffect(() => {
		if (filterData?.additionalFields) {
			setAdditionalFields(filterData?.additionalFields)
		} else {
			setAdditionalFields(defaultCheckedAdditional)
			onFilterDataChange('additionalFields', defaultCheckedAdditional)
		}
	}, [filterData?.additionalFields])

	useEffect(() => {
		const checkedOptions = filterData?.otherOptionsChecked
		if (Array.isArray(checkedOptions)) {
			const matched = checkedOptions.some((option) => DEFAULT_CHECKED_OPTIONS.includes(option))

			if (matched) {
				onFilterDataChange('otherOptionsChecked', ['expandAllMobileGroups'])
			}
		}
	}, [filterData?.otherOptionsChecked])

	useEffect(() => {
		if (isNullOrUndefined(filterData?.repeatReloadMapSeconds)) {
			onFilterDataChange('repeatReloadMapSeconds', DEFAULT_REPEAT_RELOAD_MAP_SECONDS)
		}
	}, [filterData?.repeatReloadMapSeconds])

	useEffect(() => {
		const fetchUserVariables = async () => {
			try {
				if (userVariables.length > 0) {
					return
				}
				const result = await getUserVariables(environment.apiToken)
				const newData = result?.data || []
				if (isEmpty(newData)) return

				if (!isEqual(newData, userVariables)) {
					setUserVariabless(newData)
				}

				if (!isEqual(newData, environment?.mobileUnitVariableColumns)) {
					dispatch({
						type: ENV_ACTIONS.MOBILE_UNIT_VARIABLES_COLUMNS,
						payload: newData,
					})
				}
			} catch (error) {
				logErrorMessage(error)
			}
		}

		if (isEmpty(environment?.mobileUnitVariableColumns)) {
			fetchUserVariables()
		} else {
			if (isEqual(environment?.mobileUnitVariableColumns, userVariables)) return
			setUserVariabless(environment?.mobileUnitVariableColumns || [])
		}
	}, [environment.apiToken, environment?.mobileUnitVariableColumns, userVariables])

	const combinedPrimaryKeyColumns = useMemo(() => {
		const userVariableFields = userVariables.map((variable, index) => ({
			title: variable.title,
			name: `Variable${index + 1}`,
			mobileUnitKey: `variable${index + 1}`,
			hide: false,
			type: variable.type,
		}))
		return [...mobileUnitTilePrimaryKeyColumns, ...userVariableFields]
	}, [userVariables])

	useEffect(() => {
		const fetchAdditionalFields = async () => {
			try {
				if (displayAdditionalFields.length > 0) {
					return
				}
				const result = await mobileUnitsApi.getColumns(environment.apiToken)
				setDisplayAdditionalFields(result?.data || [])
			} catch (error) {
				logErrorMessage(error)
			}
		}
		fetchAdditionalFields()
	}, [environment.apiToken, displayAdditionalFields])

	const handleSetPrimaryKey = (id) => {
		if (primaryKeyList.includes(id)) {
			onFilterDataChange(
				'primaryKeyList',
				primaryKeyList.filter((item) => item !== id)
			)
		} else {
			onFilterDataChange('primaryKeyList', [...primaryKeyList, id])
		}
	}

	const handleSetAdditionalFields = (checked, id) => {
		if (!checked) {
			const updatedFields = additionalFields.filter((item) => item !== id)
			setAdditionalFields(updatedFields)
			onFilterDataChange('additionalFields', updatedFields)
		} else {
			const updatedFields = [...additionalFields, id]
			setAdditionalFields(updatedFields)
			onFilterDataChange('additionalFields', updatedFields)
		}
	}

	return (
		<Stack spacing={2}>
			{dataGridTile?.length > 0 &&
				(!isLoading ? (
					<FormControl variant="outlined" size="small">
						<InputLabel id="datagrid-select-small-label">
							{t('common:input.datagridToConnect')}
						</InputLabel>
						<Select
							labelId="datagrid-select-small-label"
							id="datagrid-select-small"
							value={filterData?.connectedDatagridKey ?? ''}
							label={t('common:input.datagridToConnect')}
							onChange={handleSelectChange}
						>
							<MenuItem value="">
								<em>None</em>
							</MenuItem>
							{dataGridTile.map((tile) => (
								<MenuItem value={tile?.key} key={tile?.key}>
									{tile?.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				) : (
					<Skeleton variant="rectangular" height={40} />
				))}

			<CheckboxList
				title={t('common:dis.selectFields')}
				enableIcon
				enableCheckbox={true}
				onIconClick={handleSetPrimaryKey}
				checkedIconList={primaryKeyList}
				data={combinedPrimaryKeyColumns}
				onItemClick={() => {}}
				checkedList={additionalFields}
				getLabel={(option) => option.title}
				getValue={(option) => option.name}
				defaultChecked={defaultCheckedAdditional}
				onChange={handleSetAdditionalFields}
			/>
			<Box>
				<OtherOptions
					hideScroll={true}
					optionList={OTHER_OPTIONS}
					checkedOptions={filterData?.otherOptionsChecked || ['expandAllMobileGroups']}
					onChangeOptions={(value) => onFilterDataChange('otherOptionsChecked', value)}
				/>
			</Box>
			<TextField
				size="small"
				id="repeat-reload-map-value"
				label={t('common:dis.autoRefreshInterval')}
				type="number"
				value={filterData.repeatReloadMapSeconds}
				onChange={(e) => {
					const value = e.target.value
					onFilterDataChange('repeatReloadMapSeconds', value)
					if (value < MIN_REPEAT_RELOAD_MAP_VALUE || value > MAX_REPEAT_RELOAD_MAP_VALUE) {
						setRepeatReloadMapError('Repeat reload map value must be between 5 and 60')
					} else {
						setRepeatReloadMapError('')
					}
				}}
				error={Boolean(repeatReloadMapError)}
				helperText={repeatReloadMapError || ''}
				InputLabelProps={{
					shrink: true,
				}}
				inputProps={{
					inputMode: 'numeric',
					pattern: '[0-9]*',
					min: MIN_REPEAT_RELOAD_MAP_VALUE,
					max: MAX_REPEAT_RELOAD_MAP_VALUE,
				}}
			/>
		</Stack>
	)
}

export default MobileUnitsForm
